import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetActiveAdvertisement,
  GetAdvertisements,
  GetAdvertisementDetail,
  CreateAdvertisementOrder,
  GetTenantAdOrders,
  UpdateAdOrderStatus,
  GetAdminAdvertisements,
  GetAdminAdvertisementOrders,
  CreateAdvertisement,
  UpdateAdvertisement,
  DeleteAdvertisement,
} from "../Api";
import { ServiceQueryOptions } from "./query_helpers";
import { DEFAULT_STALE_TIME } from "../Constant/AppConstants";

export const AD_POPUP_STALE_TIME_MS = 5 * 60 * 1000;

export const advertisementQueryKeys = {
  activeAd: () => ["activeAdvertisement"] as const,
  ads: (params?: Parameters<typeof GetAdvertisements>[0]) =>
    ["advertisements", params] as const,
  adDetail: (adId: string) => ["advertisementDetail", adId] as const,
  tenantOrders: (params?: Parameters<typeof GetTenantAdOrders>[0]) =>
    ["tenantAdOrders", params] as const,
  adminAds: (params?: Parameters<typeof GetAdminAdvertisements>[0]) =>
    ["adminAdvertisements", params] as const,
  adminOrders: (params?: Parameters<typeof GetAdminAdvertisementOrders>[0]) =>
    ["adminAdvertisementOrders", params] as const,
};

export const invalidateAdvertisementQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["adminAdvertisements"] });
  queryClient.invalidateQueries({ queryKey: ["adminAdvertisementOrders"] });
  queryClient.invalidateQueries({ queryKey: ["tenantAdOrders"] });
  queryClient.invalidateQueries({ queryKey: ["activeAdvertisement"] });
  queryClient.invalidateQueries({ queryKey: ["advertisements"] });
};

export const useActiveAdvertisementQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: AD_POPUP_STALE_TIME_MS,
    ...options,
    queryKey: advertisementQueryKeys.activeAd(),
    queryFn: async () => {
      const res = await GetActiveAdvertisement();
      if (!res?.success) {
        return [];
      }
      return Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
    },
  });
};

export const useAdvertisementsQuery = (
  params?: Parameters<typeof GetAdvertisements>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: advertisementQueryKeys.ads(params),
    queryFn: async () => {
      const res = await GetAdvertisements(params);
      if (!res?.success) {
        return { data: [], total: 0 };
      }
      return {
        data: res.data || [],
        total: res.meta?.total || 0,
      };
    },
  });
};

export const useAdvertisementDetailQuery = (
  adId: string | undefined,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    enabled: !!adId,
    queryKey: advertisementQueryKeys.adDetail(adId || ""),
    queryFn: async () => {
      if (!adId) return null;
      const res = await GetAdvertisementDetail({ ad_id: adId });
      if (!res?.success) {
        return null;
      }
      return res.data || null;
    },
  });
};

export const useTenantAdOrdersQuery = (
  params?: Parameters<typeof GetTenantAdOrders>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: advertisementQueryKeys.tenantOrders(params),
    queryFn: async () => {
      const res = await GetTenantAdOrders(params);
      if (!res?.success) {
        return { data: [], total: 0 };
      }
      return {
        data: res.data || [],
        total: res.meta?.total || 0,
      };
    },
  });
};

export const useUpdateAdOrderStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateAdOrderStatus>[0]) =>
      UpdateAdOrderStatus(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateAdvertisementQueries(queryClient);
      }
    },
  });
};

export const useAdminAdvertisementsQuery = (
  params?: Parameters<typeof GetAdminAdvertisements>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: advertisementQueryKeys.adminAds(params),
    queryFn: async () => {
      const res = await GetAdminAdvertisements(params);
      if (!res?.success) {
        return { data: [], total: 0 };
      }
      return {
        data: res.data || [],
        total: res.meta?.total || 0,
      };
    },
  });
};

export const useAdminAdvertisementOrdersQuery = (
  params?: Parameters<typeof GetAdminAdvertisementOrders>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: advertisementQueryKeys.adminOrders(params),
    queryFn: async () => {
      const res = await GetAdminAdvertisementOrders(params);
      if (!res?.success) {
        return { data: [], total: 0 };
      }
      return {
        data: res.data || [],
        total: res.meta?.total || 0,
      };
    },
  });
};

export const useCreateAdvertisementOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateAdvertisementOrder>[0]) =>
      CreateAdvertisementOrder(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateAdvertisementQueries(queryClient);
      }
    },
  });
};

export const useCreateAdvertisementMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateAdvertisement>[0]) =>
      CreateAdvertisement(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateAdvertisementQueries(queryClient);
      }
    },
  });
};

export const useUpdateAdvertisementMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateAdvertisement>[0]) =>
      UpdateAdvertisement(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateAdvertisementQueries(queryClient);
      }
    },
  });
};

export const useDeleteAdvertisementMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteAdvertisement>[0]) =>
      DeleteAdvertisement(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateAdvertisementQueries(queryClient);
      }
    },
  });
};
